import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { ROUTES } from '../../../config/constants';
import { User, SquareCheckBig, Menu, X } from "lucide-react";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed w-full z-[999] transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-lg border-b border-gray-200'
          : 'bg-white shadow-md border-b-2 border-blue-500'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center group"
          >
            <img 
              src="/logo-optimized.png" 
              alt="MisTramites.mx" 
              className="h-14 md:h-16 w-auto transform group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <NavLink to={ROUTES.HOME} text="Inicio" />
            <NavLink to={ROUTES.CATEGORIES} text="Categorías" />
            <NavLink to={ROUTES.SUPPORT} text="Soporte" />
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleNavigation("/dashboard")}
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="hidden lg:inline">{user.name}</span>
                </button>
                <span className="hidden lg:inline-flex bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold">
                  {user.role.toUpperCase()}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-pink-700 transition-all duration-300 font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Cerrar Sesión
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleNavigation(ROUTES.LOGIN)}
                  className="text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-all duration-300 font-medium"
                >
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => handleNavigation(ROUTES.REGISTER)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Registrarse
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 animate-slide-in">
            <nav className="flex flex-col space-y-4">
              <MobileNavLink to={ROUTES.HOME} text="Inicio" onClick={() => setMobileMenuOpen(false)} />
              <MobileNavLink to={ROUTES.CATEGORIES} text="Categorías" onClick={() => setMobileMenuOpen(false)} />
              <MobileNavLink to={ROUTES.SUPPORT} text="Soporte" onClick={() => setMobileMenuOpen(false)} />
              
              {user ? (
                <>
                  <button
                    onClick={() => handleNavigation("/dashboard")}
                    className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors py-2"
                  >
                    Mi Cuenta ({user.name})
                  </button>
                  <button
                    onClick={handleLogout}
                    className="text-left bg-gradient-to-r from-red-500 to-pink-600 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleNavigation(ROUTES.LOGIN)}
                    className="text-left text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 font-medium"
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => handleNavigation(ROUTES.REGISTER)}
                    className="text-left bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    Registrarse
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const NavLink = ({ to, text }) => (
  <Link
    to={to}
    className="relative text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 group"
  >
    {text}
    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 group-hover:w-full transition-all duration-300"></span>
  </Link>
);

const MobileNavLink = ({ to, text, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="text-gray-700 hover:text-blue-600 font-medium transition-colors py-2 block"
  >
    {text}
  </Link>
);

export default Header;